import React, { useEffect } from 'react';
import { Field, FilePicker, inputCls } from '../shared/FormKit';
import {
  archivalDateError, ARCHIVAL_DATE_HINT, copyQc35Answers, Qc35ChecklistBlock,
} from '../../pages/WorkflowQc35New';
import Qc35ScreenshotExample, { QC35_SCREENSHOT_HINT } from '../workflow/Qc35ScreenshotExample';
import {
  qc35AnswerGaps, fmtQc35AnswerValue, getQc35Items, isQc35ItemShown,
  isQc35DerivedItem, qc35DerivedValue,
} from '../../pages/qc35Questions';
import { useFsAutoDetect } from '../workflow/FsDateAutoDetect';
import { formatDate, formatDateShort } from '../../utils/dateFormat';
import { CoverageCell } from '../workflow/CoverageDisplay';
import { ImportModeSwitch, useLegacyImportSetting } from './legacyImport';
import { hintOf } from '../../utils/uploadKinds';
import {
  FsSigningDateField, FsAuditOpinionField, FsAuditQuestionFields, FS_QUESTION_DEFAULTS,
} from '../../pages/WorkflowFsNew';

// Signed Financial Statements ・ QC35 的正式記錄——**三個畫面共用的一份設定**（清單、詳情、
// 建立/編輯表單）。
//
// **CaseWare & Charge Code 不在管理面**：那筆記錄沒有
// 任何自己的欄位——`casewareOpened` / `chargeCodeAssigned` 就是 engagement 主檔那兩欄，
// 而 Engagements 頁本來就改得到。放進管理面只會是第二個編輯入口，外加「記錄刪了主檔不
// 回退」這種不一致。`cwcc_records` 表與 CWCC 核准時的寫入照舊，稽核軌跡沒少；
// 後端的 kind 白名單（engagementRecords.routes.js）同步只剩 fs / qc35。
//
// 為什麼收成一份：後端 engagementRecordsRepository 早就是設定驅動的（一支
// repository 吃多種 kind），前端卻把 KINDS 抄在清單頁與詳情頁各一份。再加一支
// 表單就是三份，同一個欄位改動要改三次，而且遲早有一份會漏掉。所以欄位定義
// 只有這裡有：
//
//   columns   清單的 kind 專屬欄位（Source / Recorded 由清單頁統一補）
//   rows      詳情的閱讀列
//   form      建立/編輯表單：初值、欄位區塊、必填缺漏 gaps（[{ key, label }]，
//             key＝該格 Field 的 field=，formValidation 據此就地標紅）、
//             規則錯誤 problems（填了但不合規，modal 用 setError 呈現）、送出的 body
//
// 表單刻意不做成宣告式的欄位 DSL：FS 有簽署日 PDF 自動辨識與條件式問卷、QC35
// 有歸檔日對涵蓋案簽署日的即時檢查，硬塞進 DSL 只會生出一個比兩個表單還難懂的
// 東西。共用的是外殼（涵蓋挑選、附件增刪、送出、錯誤呈現），各 kind 只提供自己
// 的欄位區塊。

const yesNo = (v) => (v === true ? 'Yes' : v === false ? 'No' : '—');

// 簽署 FS 的上傳欄——與 workflow 表單同一支 FilePicker。副檔名也對齊
// workflow（.pdf/.doc/.docx）；後端白名單較寬是歷史包袱，不跟。
const UploadField = ({ label, hint, files, setFiles, disabled }) => (
  <Field label={label} hint={hint}>
    <FilePicker
      multiple
      kind="docsOnly"
      disabled={disabled}
      onChange={(e) => setFiles(Array.from(e.target.files || []))}
      files={files}
    />
  </Field>
);

// 編輯時上傳＝**換版**（後端 PATCH 的 supersedeDocs）：舊版標 previous version
// 留在記錄上，不會消失——說在欄位上，不是存完才發現。
const REPLACE_HINT = 'Uploading replaces the current document — the previous version stays on record.';

export const RECORD_KINDS = {
  // ══ Signed Financial Statements ════════════════════════════════════════════════════════════
  fs: {
    key: 'fs',
    label: 'Signed Financial Statements',
    title: 'Signed Financial Statements Records',
    subtitle: 'Signed financial statements on file.',
    listTo: '/qm/records/fs',
    searchPlaceholder: 'Search request no or engagement…',
    emptyText: 'No records yet — approvals create them, or add one with “New Record”.',
    coverage: 'single',

    columns: [
      { key: 'coverage', label: 'Engagement(s)', render: (r, ctx) => <CoverageCell record={r} ctx={ctx} /> },
      { key: 'signingDate', label: 'Signing Date', render: (r) => (r.signingDate ? formatDateShort(r.signingDate) : '—') },
      // 只數現行版——被換掉的舊版還在列上（supersede 不硬刪），但不是「文件數」。
      { key: 'files', label: 'Documents', render: (r) => (r.files || []).filter((f) => !f.supersededAt).length },
    ],

    rows: (r) => [
      ['Signing Date', r.signingDate ? formatDate(r.signingDate) : '—'],
      ['Audit Opinion', r.auditOpinion || '—'],
      ['Technological Tools Used', yesNo(r.techToolsUsed)],
      ...(r.techToolsAreas ? [['Tool Deployment Areas', r.techToolsAreas]] : []),
      ['Restatement', yesNo(r.restatement)],
      ...(r.restatementDesc ? [['Restatement Description', r.restatementDesc]] : []),
    ],

    form: {
      title: 'Signed Financial Statements Record',
      // 建立時可一次建同集團、FYE 同年的多家（一家一個分頁，EngagementRecordFormModal）。
      batchCreate: true,
      // 分頁上方的 Partner／Manager 框多列目前簽署日（同 workflow FS New）。
      showSignOff: true,
      // 新記錄的問卷從 workflow 同一組預設答案開始；編輯照記錄現值（未答＝兩個都不勾）。
      initial: (record) => ({
        engagementIds: record ? (record.engagements || []).map((e) => e.engagementId).filter(Boolean) : [],
        signingDate: record ? (record.signingDate || '') : '',
        auditOpinion: record ? (record.auditOpinion || '') : '',
        ...(record ? {
          techToolsUsed: record.techToolsUsed === true ? 'yes' : record.techToolsUsed === false ? 'no' : '',
          techToolsAreas: record.techToolsAreas || '',
          restatement: record.restatement === true ? 'yes' : record.restatement === false ? 'no' : '',
          restatementDesc: record.restatementDesc || '',
        } : FS_QUESTION_DEFAULTS),
        remarks: record ? (record.remarks || '') : '',
      }),
      // 欄位直接用 workflow FS 表單那幾支（簽署日、Audit Opinion、問卷兩題），
      // 簽署日 PDF 自動偵測也共用 useFsAutoDetect。沒有 Archival deadline 欄位——
      // 後端根本不收這個值（期限是簽署日 + 期間天數的衍生值，人工覆寫在
      // Engagements 頁），放了只是一個存不進去的假欄位。
      Fields: ({ form, setField, files, setFiles, isEdit, record }) => {
        const detect = useFsAutoDetect({
          files,
          onDetected: (d) => setField('signingDate', d),
        });
        // Mandatory on Management entries; a record created by the data-import
        // tool carries no opinion and may stay blank when edited here.
        const opinionRequired = !(record && record.source === 'import');
        // 掃描中要擋 Save——外殼讀 form.detectBusy（toBody 不送這個 key）。
        // eslint-disable-next-line react-hooks/exhaustive-deps
        useEffect(() => { setField('detectBusy', detect.busy); }, [detect.busy]);
        return (
        <>
          <UploadField
            label="Upload Signed Financial Statements"
            hint={`${hintOf('docsOnly', { multiple: true })} Upload a PDF to enable signing-date auto-detection.${isEdit ? ` ${REPLACE_HINT}` : ''}`}
            files={files}
            setFiles={setFiles}
            disabled={detect.busy}
          />
          <FsSigningDateField
            value={form.signingDate}
            onChange={(v) => setField('signingDate', v)}
            detect={detect}
            hint="The archival deadline follows as the signing date plus the number of days set for the period it falls in — adjust it on the engagement if needed."
          />
          <FsAuditOpinionField
            value={form.auditOpinion}
            onChange={(v) => setField('auditOpinion', v)}
            required={opinionRequired}
          />
          <FsAuditQuestionFields
            techToolsUsed={form.techToolsUsed} setTechToolsUsed={(v) => setField('techToolsUsed', v)}
            techToolsAreas={form.techToolsAreas} setTechToolsAreas={(v) => setField('techToolsAreas', v)}
            restatement={form.restatement} setRestatement={(v) => setField('restatement', v)}
            restatementDesc={form.restatementDesc} setRestatementDesc={(v) => setField('restatementDesc', v)}
          />
        </>
        );
      },
      gaps: (form, { record } = {}) => {
        const gaps = [];
        if (form.engagementIds.length === 0) gaps.push({ key: 'engagementIds', label: 'Engagement' });
        if (!form.signingDate) gaps.push({ key: 'signingDate', label: 'Audit Report Signing Date' });
        if (!form.auditOpinion && !(record && record.source === 'import')) gaps.push({ key: 'auditOpinion', label: 'Audit Opinion' });
        return gaps;
      },
      problems: () => [],
      toBody: (form, fd) => {
        fd.set('engagementIds', JSON.stringify(form.engagementIds));
        fd.set('signingDate', form.signingDate);
        fd.set('auditOpinion', form.auditOpinion);
        fd.set('techToolsUsed', form.techToolsUsed === '' ? '' : String(form.techToolsUsed === 'yes'));
        fd.set('techToolsAreas', form.techToolsAreas);
        fd.set('restatement', form.restatement === '' ? '' : String(form.restatement === 'yes'));
        fd.set('restatementDesc', form.restatementDesc);
      },
    },
  },

  // ══ QC35 ═════════════════════════════════════════════════════════════════
  qc35: {
    key: 'qc35',
    label: 'QC35 Form',
    title: 'QC35 Form Records',
    subtitle: 'QC35 Form file archival records.',
    listTo: '/qm/records/qc35',
    searchPlaceholder: 'Search request no or engagement…',
    emptyText: 'No records yet — approvals create them, or add one with “New Record”.',
    coverage: 'single',
    // QC35 核准＝歸檔完成：歸檔日落在這筆記錄上，涵蓋案同時標記完成。
    // 手動建立照抄。

    columns: [
      { key: 'coverage', label: 'Engagement(s)', render: (r, ctx) => <CoverageCell record={r} ctx={ctx} /> },
      { key: 'archivalDate', label: 'Archival Date', render: (r) => (r.archivalDate ? formatDateShort(r.archivalDate) : '—') },
      // 只數現行版——被換掉的舊版還在列上（supersede 不硬刪），但不是「文件數」。
      { key: 'files', label: 'Documents', render: (r) => (r.files || []).filter((f) => !f.supersededAt).length },
    ],

    rows: (r) => [
      ['Archival Date', r.archivalDate ? formatDate(r.archivalDate) : '—'],
      // 同 QC6/QC7：這格只回答簽署檔是哪一份，checklist 為何空白自己一列。
      ...(r.legacyImport ? [
        ['Signed Form', r.signedFormPending
          ? <span className="text-slate-500">Not uploaded yet</span>
          : ((r.files || []).find((f) => f.docType === 'qc35' && !f.supersededAt)?.originalName || 'Uploaded')],
        ['File Archival Checklist', <span className="text-slate-500">Not filled in — the answers are on the signed form</span>],
      ] : []),
      // Form QC35 checklist（May 2026）逐題閱讀列；legacy 記錄沒有答案就不列，
      // 條件列（回收率說明）只在成立時列。
      // 衍生列（Date of Audit Report）讀涵蓋案連結的 FS 記錄，不讀答案——
      // 表上其他列才是填表人答的。
      ...(r.answers ? getQc35Items()
        .filter((q) => isQc35ItemShown(q, r.answers))
        .map((q) => {
          const v = isQc35DerivedItem(q)
            ? qc35DerivedValue(q, (r.engagements || [])[0])
            : r.answers[q.id];
          return [q.label, q.type === 'date' && v ? formatDate(v) : fmtQc35AnswerValue(q, v)];
        }) : []),
    ],

    form: {
      title: 'QC35 Form Record',
      // 建立時可一次建同集團、FYE 同年的多家（一家一個分頁，EngagementRecordFormModal）。
      batchCreate: true,
      // 分頁上的「Copy checklist answers from」：抄來源分頁的 checklist，依目的那家的
      // QC 現況重新帶入自動值。匯入模式（簽署表）的分頁沒有 checklist，不抄。
      copyFrom: (src, dst) => (src.legacyImport || dst.legacyImport
        ? dst
        : { ...dst, answers: copyQc35Answers(src.answers, dst.qcContext || null) }),
      initial: (record) => ({
        engagementIds: record ? (record.engagements || []).map((e) => e.engagementId).filter(Boolean) : [],
        archivalDate: record ? (record.archivalDate || '') : '',
        answers: record ? (record.answers || {}) : {},
        remarks: record ? (record.remarks || '') : '',
        // CaseWare 截圖的待上傳檔（File 物件）——toBody 以 `screenshot` 欄位
        // 送出，不是 body 值；放 form 裡是因為外殼的 files/setFiles 只有一組。
        screenshotFiles: [],
        // Legacy import：上傳簽署的 Form QC35（`files`，doc_type qc35）、checklist
        // 留空。建立時決定，之後唯讀；補齊 checklist 走 QC35 Form Amendment。
        legacyImport: record ? record.legacyImport === true : false,
        signedForm: [],
      }),
      // `copy`（只有多家建立時才有）：{ sources, onCopy }，掛在 checklist 標題下。
      Fields: ({ form, setField, options, isEdit, record = null, copy = null }) => {
        const { enabled: legacyEnabled, missingSignedFormAllowed } = useLegacyImportSetting();
        const currentSigned = (record?.files || []).filter((f) => f.docType === 'qc35' && !f.supersededAt);
        // Editing a record that never got its signed form: this upload is the
        // first version, not a replacement.
        const pendingUpload = isEdit && currentSigned.length === 0;
        return (
        <>
          {!isEdit && legacyEnabled === true && (
            <ImportModeSwitch
              value={form.legacyImport}
              onChange={(v) => {
                if (v === form.legacyImport) return;
                setField('legacyImport', v); setField('answers', {}); setField('signedForm', []);
              }}
              formName="File archival checklist"
            />
          )}
          {form.legacyImport && (
            <Field
              label={pendingUpload ? 'Upload the Signed Form QC35' : isEdit ? 'Replace the Signed Form QC35' : 'Signed Form QC35'}
              required={!isEdit && !missingSignedFormAllowed}
              field="signedForm"
              hint={pendingUpload
                ? 'This record was imported without its signed form. Attach the signed PDF here — it becomes the first version on file.'
                : isEdit
                  ? REPLACE_HINT
                  : missingSignedFormAllowed
                    ? 'Signed and approved form, PDF. Can be uploaded later from Edit. The checklist below stays empty; complete it through a QC35 Form Amendment.'
                    : 'Signed and approved form, PDF. The checklist below stays empty; complete it through a QC35 Form Amendment.'}
            >
              {currentSigned.length > 0 && (
                <p className="text-xs text-slate-500 mb-1">Current: {currentSigned.map((f) => f.originalName).join(', ')}</p>
              )}
              <FilePicker
                kind="pdfOnly"
                onChange={(e) => setField('signedForm', Array.from(e.target.files || []).slice(0, 1))}
                files={form.signedForm || []}
              />
            </Field>
          )}
          {/* 歸檔日是這筆記錄的核心事實——涵蓋案的「歸檔了沒、哪天歸檔」
              全由它講。不得早於涵蓋案的簽署日——選到的當下就在欄位下講，
              與 workflow 的 QC35 New／Modify 同一句、同一把尺（後端仍把關）。 */}
          <Field
            label="Archival Date"
            required
            field="archivalDate"
            hint={ARCHIVAL_DATE_HINT}
            error={archivalDateError(form.archivalDate, (options || []).filter((e) => form.engagementIds.includes(e.id)))}
          >
            <input
              type="date"
              value={form.archivalDate}
              onChange={(e) => setField('archivalDate', e.target.value)}
              className={inputCls}
            />
          </Field>
          {/* CaseWare 截圖（Form QC35 May 2026 的必附文件）——QC35 記錄唯一的
              上傳欄（QC35 本身就是下面那張 checklist）；救援入口選填：legacy
              記錄在這裡補，重新上傳＝換版。 */}
          <Field
            label="CaseWare Audit Working Papers Screenshot"
            field="screenshot"
            hint={`${QC35_SCREENSHOT_HINT}${isEdit ? ` ${REPLACE_HINT}` : ''}`}
          >
            <FilePicker
              multiple
              kind="images"
              onChange={(e) => setField('screenshotFiles', Array.from(e.target.files || []))}
              files={form.screenshotFiles || []}
            />
            <Qc35ScreenshotExample />
          </Field>
          {/* Form QC35 checklist（May 2026）——與 workflow 的 New／Modify 同一
              份題庫與元件；表頭的客戶名／年結引用涵蓋案，其餘自填。匯入的
              記錄不在這裡填——它的表就是上傳的簽署 PDF。 */}
          {!form.legacyImport && (
            <Qc35ChecklistBlock
              answers={form.answers}
              onChange={(v) => setField('answers', v)}
              engagements={(options || []).filter((e) => form.engagementIds.includes(e.id))}
              onContext={(c) => setField('qcContext', c)}
              copySources={copy ? copy.sources : []}
              onCopy={copy ? copy.onCopy : undefined}
            />
          )}
        </>
        );
      },
      gaps: (form, { record = null, allowMissingSignedForm = false } = {}) => {
        const gaps = [];
        if (form.engagementIds.length === 0) gaps.push({ key: 'engagementIds', label: 'Engagement(s)' });
        if (!form.archivalDate) gaps.push({ key: 'archivalDate', label: 'Archival Date' });
        if (form.legacyImport) {
          // The signed PDF is required on create unless Admin Settings allow
          // "Signed form pending"; on edit it is never required (the record
          // either has one or is waiting for its first upload).
          const hasSigned = (record?.files || []).some((f) => f.docType === 'qc35' && !f.supersededAt);
          if ((form.signedForm || []).length === 0 && !hasSigned && !record && !allowMissingSignedForm) {
            gaps.push({ key: 'signedForm', label: 'Signed Form QC35 (PDF)' });
          }
        } else {
          // checklist 逐題必填（題庫同一把尺，格子自己標紅）；EQR 時數與未達
          // 指引的 Comments 看涵蓋案的 QC 現況（checklist 元件抓回來放 form.qcContext）。
          gaps.push(...qc35AnswerGaps(form.answers, form.qcContext || null));
        }
        return gaps;
      },
      // 歸檔日早於簽署日：欄位下已即時標紅，Save 時仍擋（同一句進橫幅）。
      problems: (form, { selected = [] } = {}) => {
        const dateErr = archivalDateError(form.archivalDate, selected);
        return dateErr ? [dateErr] : [];
      },
      toBody: (form, fd) => {
        fd.set('engagementIds', JSON.stringify(form.engagementIds));
        fd.set('archivalDate', form.archivalDate);
        fd.set('legacyImport', form.legacyImport ? 'true' : 'false');
        if (form.legacyImport) {
          // 匯入的記錄：簽署 PDF 走 `files`（doc_type qc35），checklist 不送。
          (form.signedForm || []).slice(0, 1).forEach((f) => fd.append('files', f));
        } else {
          fd.set('answers', JSON.stringify(form.answers || {}));
        }
        (form.screenshotFiles || []).forEach((f) => fd.append('screenshot', f));
      },
    },
  },
};

export const recordKind = (kind) => RECORD_KINDS[kind] || null;

// 記錄是誰產生的。人工建立的沒有單號，來源欄留空。
export const RECORD_SOURCE_LABEL = {
  'workflow:fs': 'Signed Financial Statements approval',
  'workflow:qc35': 'QC35 Form approval',
  manual: '',
};
